/**
 * Paced pointer drag for libraries that implement drag-and-drop on raw
 * pointer/mouse events (dnd-kit, react-aria, pragmatic-dnd) rather than the
 * HTML5 drag API.
 *
 * Those libraries gate activation on post-mousedown movement (e.g. dnd-kit's
 * `activationConstraint: {distance: 5}`) and track the pointer with
 * rAF-throttled collision detection — a single-jump down→move→up either never
 * activates or releases before the library has registered the target, so the
 * drop resolves at the activation position (a no-op). The gesture:
 *
 *   1. press at the start point,
 *   2. nudge a few px toward the target so distance-activation fires, then
 *      hold a beat so the library mounts its drag state,
 *   3. walk to the target in paced steps so rAF-throttled dragover/collision
 *      logic tracks the pointer along the way,
 *   4. hold over the target before release so the final slot is the one the
 *      library actually registered.
 *
 * @module playwright-client/pointer-drag
 */
import type { Page } from 'playwright';
import { POINTER_DRAG } from '../constants.js';

export interface PointerDragPoint {
  x: number;
  y: number;
}

export interface PointerDragHooks {
  /** Called at each intermediate position (e.g. to sync a cursor overlay). */
  onStep?: (x: number, y: number) => Promise<void>;
}

export async function performPointerDrag(
  page: Page,
  from: PointerDragPoint,
  to: PointerDragPoint,
  hooks?: PointerDragHooks
): Promise<void> {
  await page.mouse.move(from.x, from.y);
  await page.mouse.down();

  // Nudge toward the target (or right, for a zero-length path) so
  // distance-based activation constraints fire before the walk starts.
  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  const nudgeX = from.x + Math.cos(angle) * POINTER_DRAG.ACTIVATION_NUDGE_PX;
  const nudgeY = from.y + Math.sin(angle) * POINTER_DRAG.ACTIVATION_NUDGE_PX;
  await page.mouse.move(nudgeX, nudgeY, { steps: 2 });
  await page.waitForTimeout(POINTER_DRAG.ACTIVATION_HOLD_MS);

  for (let i = 1; i <= POINTER_DRAG.PATH_STEPS; i++) {
    const t = i / POINTER_DRAG.PATH_STEPS;
    const x = nudgeX + (to.x - nudgeX) * t;
    const y = nudgeY + (to.y - nudgeY) * t;
    await hooks?.onStep?.(x, y);
    await page.mouse.move(x, y);
    await page.waitForTimeout(POINTER_DRAG.STEP_DELAY_MS);
  }

  // Hold over the target so the library's (possibly rAF-deferred) dragover
  // state reflects the release position, then drop.
  await page.waitForTimeout(POINTER_DRAG.DROP_HOLD_MS);
  await page.mouse.up();
}
